'use client';

import { useCallback, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  StorefrontProductCard,
  type StorefrontCardCopy,
  type StorefrontCardVariant,
  type StorefrontProduct,
} from '@/components/product/storefront-product-card';

export type LoadMoreCopy = StorefrontCardCopy & {
  loadMore: string;
  loading: string;
  loadFailed: string;
  remainingLabel: string;
};

/**
 * Rendert het productgrid met een "Toon meer"-knop. De eerste pagina komt
 * server-rendered binnen via props; vervolgbatches worden client-side
 * opgehaald uit /api/products/browse met dezelfde filters en sortering.
 */
export function ProductGridWithLoadMore({
  initialProducts,
  total,
  pageSize,
  locale,
  countryCode,
  category,
  search,
  priceParam = '',
  sortParam = '',
  copy,
  variant = 'default',
}: {
  initialProducts: StorefrontProduct[];
  total: number;
  pageSize: number;
  locale: string;
  countryCode: string;
  category: string;
  search: string;
  priceParam?: string;
  sortParam?: string;
  copy: LoadMoreCopy;
  variant?: StorefrontCardVariant;
}) {
  const [products, setProducts] = useState<StorefrontProduct[]>(initialProducts);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const remaining = Math.max(0, total - products.length);

  const loadMore = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setFailed(false);
    try {
      const params = new URLSearchParams({
        locale,
        category,
        offset: String(products.length),
        limit: String(pageSize),
      });
      if (search) params.set('q', search);
      if (priceParam) params.set('prijs', priceParam);
      if (sortParam) params.set('sortering', sortParam);
      const response = await fetch(`/api/products/browse?${params.toString()}`);
      if (!response.ok) throw new Error(`browse failed: ${response.status}`);
      const data = (await response.json()) as { products: StorefrontProduct[] };
      setProducts((current) => {
        const seen = new Set(current.map((product) => product.id));
        return [...current, ...data.products.filter((product) => !seen.has(product.id))];
      });
    } catch (error) {
      console.error('Load more products failed:', error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [category, loading, locale, pageSize, priceParam, products.length, search, sortParam]);

  const isStickerVariant = variant === 'stickers';
  const isBoardVariant = variant === 'boards';
  const isCanvasVariant = variant === 'canvas';

  return (
    <>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        {products.map((product) => (
          <StorefrontProductCard
            key={product.id}
            product={product}
            locale={locale}
            countryCode={countryCode}
            copy={copy}
            variant={variant}
          />
        ))}
      </div>
      {remaining > 0 && (
        <div className="mt-8 flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={loadMore}
            disabled={loading}
            className={`inline-flex items-center gap-2 rounded-full px-8 py-3 text-sm font-black text-white transition disabled:opacity-60 ${isStickerVariant ? 'bg-teal-700 hover:bg-teal-800' : isBoardVariant ? 'bg-blue-700 hover:bg-blue-800' : isCanvasVariant ? 'bg-amber-700 hover:bg-amber-800' : 'bg-slate-950 hover:bg-slate-800'}`}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {copy.loading}
              </>
            ) : (
              <>
                {copy.loadMore}
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold">{remaining}</span>
              </>
            )}
          </button>
          <p className="text-xs font-medium text-slate-500">
            {products.length} / {total} {copy.remainingLabel}
          </p>
          {failed && (
            <p className="text-xs font-semibold text-rose-600">{copy.loadFailed}</p>
          )}
        </div>
      )}
    </>
  );
}
